import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { PublicContainer } from '@/containers/PublicContainer';
import { SocialNetworks } from '@/containers/SocialNetworks';
import { STEPS } from '@/pages/AuthPage/data';
import Notify from '@/components/Notification';
import { markRefreshSessionHint } from '@/helpers/authSession';
import {
  clearTempToken,
  clearTempUserData,
  getAuthData,
  getTempUserData,
  saveTempToken,
  saveTempUserData,
} from '@/pages/AuthPage/utils';
import { APP_LINKS } from '@/helpers/links';
import { QUIZ_SLUGS } from '@/helpers/constants';
import { useReferrals } from '@/providers/referral';
import { useGetQuizRedirectInfo } from '@/query/queries/quiz/useGetQuizRedirectInfo';
import { ReCaptchaProvider } from '@/providers/recaptcha';
import { getLastRoute, removeLastRoute, scrollTop } from '@/helpers/utils';
import { useUserStore } from '@/store/userStore';
import { StepMain } from './components/StepMain';
import { StepEmailOtp } from './components/StepEmailOtp';
import { StepPhoneOtp } from './components/StepPhoneOtp';
import { StepReferral } from './components/StepReferral';
import { StepVeriff } from './components/StepVeriff';
import s from './index.module.scss';

const AuthPage = () => {
  const navigate = useNavigate();
  const { currentRefCode } = useReferrals();
  const { t } = useTranslation();
  const { accessToken, setAccessToken } = useUserStore(state => state);
  const [state, setState] = useState(getAuthData());
  const [step, setStep] = useState(state?.data?.step || STEPS.main);
  const { refetch: getQuizRedirectInfo } = useGetQuizRedirectInfo(
    QUIZ_SLUGS.clientOnboarding,
  );

  const setAuthenticatedSession = accessToken => {
    markRefreshSessionHint();
    setAccessToken(accessToken);
  };

  useEffect(() => {
    scrollTop();
  }, [step]);

  const redirectLastRoute = useCallback(() => {
    const lastRoute = getLastRoute();
    navigate(lastRoute || APP_LINKS.dashboard, { replace: true });
    removeLastRoute();
  }, [navigate]);

  useEffect(() => {
    if (accessToken && !getTempUserData()) {
      redirectLastRoute();
    }
  }, [accessToken, redirectLastRoute]);

  const handleQuizRedirect = async () => {
    const res = await getQuizRedirectInfo();
    if (res?.data?.quiz_enabled && res?.data?.redirect_url) {
      return (window.location.href = res.data.redirect_url);
    }
    setStep(STEPS.veriff);
  };

  const onEmailSubmit = ({ email, temp_token, is_new_user }) => {
    const userData = { email, step: STEPS.otp_email, is_new_user };
    saveTempToken(temp_token);
    saveTempUserData(userData);
    setState({
      token: temp_token,
      data: userData,
    });
    setStep(STEPS.otp_email);
  };

  const onEmailOtpSubmit = response => {
    if (state.data) {
      // Registration flow
      if (state.data.is_new_user) {
        clearTempToken();
        setAuthenticatedSession(response.access_token);
        if (!currentRefCode) {
          saveTempUserData({ ...state.data, step: STEPS.referral });
          setStep(STEPS.referral);
        } else {
          saveTempUserData({
            ...state.data,
            step: STEPS.veriff,
          });
          handleQuizRedirect();
        }
        return;
      }

      // Login flow
      if (response.temp_token) {
        saveTempUserData({ ...state.data, step: STEPS.phone });
        saveTempToken(response.temp_token);
        return setStep(STEPS.phone);
      }

      if (response.access_token) {
        clearTempUserData();
        setAuthenticatedSession(response.access_token);
      }
    }
  };

  const onSocialSuccess = res => {
    if (res?.temp_token) {
      saveTempUserData({ ...state.data, step: STEPS.phone });
      saveTempToken(res.temp_token);
      return setStep(STEPS.phone);
    }

    if (res?.access_token) {
      setAuthenticatedSession(res.access_token);
      // Decode JWT to recognize if user registering or logging in
      const decodedData = jwtDecode(res.access_token);

      if (decodedData.user?.is_new_user) {
        if (!currentRefCode) {
          saveTempUserData({ ...state.data, step: STEPS.referral });
          setStep(STEPS.referral);
        } else {
          saveTempUserData({ ...state.data, step: STEPS.veriff });
          handleQuizRedirect();
        }
        return;
      }

      Notify.success({
        text: t('kit:notify.welcome', {
          firstName: decodedData.user?.first_name,
          lastName: decodedData.user?.last_name,
        }),
      });

      redirectLastRoute();
    }
  };

  const onNextReferral = () => {
    saveTempUserData({ ...state.data, step: STEPS.veriff });
    clearTempToken();
    handleQuizRedirect();
  };

  const onPhoneOtpSubmit = response => {
    if (response.access_token) {
      clearTempUserData();
      setAuthenticatedSession(response.access_token);
    }
  };

  return (
    <ReCaptchaProvider>
      <PublicContainer>
        <div className={s.wrapper}>
          <div className="g-container">
            {step === STEPS.main && (
              <StepMain onEmailSubmit={onEmailSubmit}>
                <SocialNetworks onSuccess={onSocialSuccess} />
              </StepMain>
            )}
            {step === STEPS.otp_email && (
              <StepEmailOtp
                onBack={() => setStep(STEPS.main)}
                onSuccess={onEmailOtpSubmit}
              />
            )}
            {step === STEPS.referral && (
              <StepReferral nextStep={onNextReferral} />
            )}
            {step === STEPS.phone && (
              <StepPhoneOtp onSuccess={onPhoneOtpSubmit} />
            )}
            {step === STEPS.veriff && <StepVeriff />}
          </div>
        </div>
      </PublicContainer>
    </ReCaptchaProvider>
  );
};

export default AuthPage;
